import type { DriverHosClocks } from "@storyboard/web-client";
import type { DriverHosSummary, HosClockKind } from "@/types/driverHos";

/**
 * The order the HOS block lays the clocks out in, left to right then top to bottom: 8H Rest and 11H Drive, then
 * 14H Shift and 70H Cycle.
 */
export const HOS_CLOCK_ORDER: readonly HosClockKind[] = ["break", "drive", "shift", "cycle"];

/**
 * Folds one driver's hos-clocks answer into what the HOS block shows, or null for a driver with no timer.
 *
 * A clock the timer does not carry is left out rather than zeroed, so a missing reading never reads as a driver out
 * of hours. The recap comes out earliest day first whatever order the wire sent.
 */
export function summarizeDriverHos(hos: DriverHosClocks | null): DriverHosSummary | null {
  if (!hos) return null;
  return {
    asOf: hos.asOf,
    timeZone: hos.timeZone,
    clocks: HOS_CLOCK_ORDER.flatMap((kind) => {
      const clock = hos.clocks[kind];
      if (!clock) return [];
      return [
        {
          kind,
          secondsLeft: clock.secondsLeft,
          running: clock.running,
          nextResetAt: clock.nextResetAt,
          limiting: hos.limiting === kind,
        },
      ];
    }),
    recap: [...hos.recap]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(({ date, gainedSeconds }) => ({ date, gainedSeconds })),
  };
}
